import {
  getRangesForDay,
  hhmmToMinutes,
  type DayIndex,
  type ServiceDisplayDayPart,
} from '../day-part/utils';
import type { DayPartThreshold, WidgetHorizon } from './types';

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const MAX_RETENTION_DURATION_REAL_TIME_WIDGETS = ONE_DAY_MS;

export interface HorizonContext {
  /** Current time in epoch ms (typically `Date.now()`). */
  now: number;
  /** The current local Date corresponding to `now`. Passed in so callers may control timezone for tests. */
  localNow: Date;
  /** Day-part thresholds attached to the widget (for `currentDayPart`). */
  widgetThresholds: readonly DayPartThreshold[];
  /** All day parts the customer has defined. */
  allDayParts: readonly ServiceDisplayDayPart[];
}

/** Epoch ms for the beginning of the local hour containing `d`. */
export function startOfLocalHour(d: Date): number {
  const start = new Date(d);
  start.setMinutes(0, 0, 0);
  return start.getTime();
}

/** Epoch ms for local midnight of the day containing `d`. */
export function startOfLocalDay(d: Date): number {
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
}

/** Epoch ms for `minutesSinceMidnight` on the same local day as `d`. */
function localMinutesToEpochMs(d: Date, minutesSinceMidnight: number): number {
  const start = new Date(d);
  start.setHours(0, minutesSinceMidnight, 0, 0);
  return start.getTime();
}

/**
 * Returns the day part currently active for the widget — i.e. one of the widget's
 * day-part-specific thresholds whose schedule covers "now" — or `null` when none is
 * active. Only considers day parts associated with the widget via a threshold override.
 */
export function getActiveDayPart(ctx: HorizonContext): ServiceDisplayDayPart | null {
  if (!ctx.widgetThresholds.length || !ctx.allDayParts.length) return null;

  const dayIndex = String(ctx.localNow.getDay()) as DayIndex;
  const minutesNow = ctx.localNow.getHours() * 60 + ctx.localNow.getMinutes();

  for (const t of ctx.widgetThresholds) {
    const dp = ctx.allDayParts.find((d) => d.id === t.dayPartsId);
    if (!dp) continue;
    const ranges = getRangesForDay(dp.dayParts, dayIndex);
    if (ranges.length === 0) continue;
    const start = hhmmToMinutes(ranges[0].start);
    const end = hhmmToMinutes(ranges[0].end);
    if (minutesNow >= start && minutesNow < end) {
      return dp;
    }
  }
  return null;
}

/**
 * Returns the epoch ms at which the currently-active day part for the widget started,
 * or `null` when no day part associated with the widget is active right now.
 */
export function getActiveDayPartStartMs(ctx: HorizonContext): number | null {
  const dp = getActiveDayPart(ctx);
  if (!dp) return null;

  const dayIndex = String(ctx.localNow.getDay()) as DayIndex;
  const ranges = getRangesForDay(dp.dayParts, dayIndex);
  if (ranges.length === 0) return null;
  const start = hhmmToMinutes(ranges[0].start);
  return localMinutesToEpochMs(ctx.localNow, start);
}

/**
 * Returns the timestamp (epoch ms) marking the start of the time bucket considered
 * by the given horizon, or `null` for `realtime` (which uses currently active events,
 * not a fixed window).
 *
 * For `currentDayPart`, falls back to the `1h` window when no associated day part is active.
 */
export function getHorizonStartMs(horizon: WidgetHorizon, ctx: HorizonContext): number | null {
  switch (horizon) {
    case 'realtime':
      return null;
    case '15m':
      return ctx.now - FIFTEEN_MINUTES_MS;
    case '1h':
      return ctx.now - ONE_HOUR_MS;
    case 'currentHour':
      return startOfLocalHour(ctx.localNow);
    case 'currentDay':
      return startOfLocalDay(ctx.localNow);
    case 'currentDayPart': {
      const start = getActiveDayPartStartMs(ctx);
      return start ?? ctx.now - ONE_HOUR_MS;
    }
  }
}

/**
 * Max number of ms we may need to look backwards for a single widget given its horizon
 * and configured day-part thresholds. Used to size the historical fetch and the
 * event-retention window so widgets always have enough data.
 */
export function getWidgetLookbackMs(
  horizon: WidgetHorizon,
  widgetThresholds: readonly DayPartThreshold[],
  allDayParts: readonly ServiceDisplayDayPart[],
): number {
  switch (horizon) {
    case 'realtime':
      // real time counts all active events, so this needs to be longer than the longest expected event duration
      return MAX_RETENTION_DURATION_REAL_TIME_WIDGETS;
    case '15m':
      return FIFTEEN_MINUTES_MS;
    case '1h':
    case 'currentHour':
      return ONE_HOUR_MS;
    case 'currentDay':
      return ONE_DAY_MS;
    case 'currentDayPart': {
      let max = ONE_HOUR_MS;
      for (const t of widgetThresholds) {
        const dp = allDayParts.find((d) => d.id === t.dayPartsId);
        if (!dp) continue;
        for (const rangeList of Object.values(dp.dayParts ?? {})) {
          for (const r of rangeList ?? []) {
            const lenMs = (hhmmToMinutes(r.end) - hhmmToMinutes(r.start)) * 60 * 1000;
            if (lenMs > max) max = lenMs;
          }
        }
      }
      return max;
    }
  }
}
